import BaseMenuListCollapse, { MenuItems } from '@components/menu-list-collapse/BaseMenuListCollapse';
import { pushError, pushSuccess } from '@components/toast';
import { pmc8266CameraHttpController, ptzCameraHttpController, solar4gCameraHttpController } from '@controllers/index';
import useMedia from '@hooks/useMedia';
import { Autorenew } from '@mui/icons-material';
import {
    Box,
    CircularProgress,
    Divider,
    Grid,
    IconButton,
    Stack,
    SxProps,
    Tooltip,
    Typography
} from '@mui/material';
import useMileSightPMC8266, { SectionsCollapsePMC8266 } from '@pages/cameras/hook/useMilesightPMC8266';
import useMileSightPTZ, { SectionsCollapsePTZ } from '@pages/cameras/hook/useMilesightPTZ';
import useMileSightSolar from '@pages/cameras/hook/useMilesightSolar';
import useMileSightSP111, { SectionsCollapseSP111 } from '@pages/cameras/hook/useMilesightSP111';
import color from '@theme/Colors';
import { useMutation, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { useBackdrop } from 'src/providers/BackdropProvider';
import { useCameraDetailContext } from '../../../hook/CameraDetailsProvider';
import CaptureManagementMilesightCameraSolar from '../CaptureManagementMilesightCameraSolar';
import CellularMilesightCameraSolar from '../CellularMilesightCameraSolar';
import DateAndTimeMilesightCameraSolar from '../DateAndTimeMilesightCameraSolar';
import { MQTTConfigExpectValue } from '../helpers';
import MileSightPMC8266Page from '../tabsGeozonesDetail/pmc8266/milesight';
import MileSightPtzPage from '../tabsGeozonesDetail/ptz/milesight';
import MileSightSP111Page from '../tabsGeozonesDetail/sp111/milesight';
import StatusManagementMilesightCameraSolar from '../StatusManagementMilesightCameraSolar';

const menuItemsMileSightCameraSolar: MenuItems[] = [
    {
        title: 'Cellular',
        items: [
            { name: 'Cellular status', id: 'cellular-status' },
            { name: 'Cellular settings', id: 'cellular-settings' },
            { name: 'Wake up settings', id: 'wake-up-settings' },
        ],
    },
        {
        title: 'Status',
        items: [
            { name: 'External battery pack', id: 'external-battery-pack' },
        ],
    },
    {
        title: 'Capture management',
        items: [
            { name: 'Image Settings', id: 'image-settings' },
            { name: 'Trigger Capture', id: 'trigger-capture' },
            { name: 'Continuous capture', id: 'continuous-capture' },
            { name: 'Photo capture config', id: 'photo-capture-config' },
            { name: 'Upload setting', id: 'upload-setting' },
        ],
    },
    {
        title: 'Date & Time',
        items: [
            { name: 'Set the system time', id: 'set-system-time' },
            { name: 'Current system time', id: 'current-system-time' },
        ],
    },
];

const menuItemsMileSightCameraPTZ: MenuItems[] = [
    {
        title: 'Basic information',
        items: [
            {
                name: 'Basic',
                id: 'basic',
            },
            { name: 'PTZ OSD', id: 'PTZ_OSD' },
            {
                name: 'Others',
                id: 'others',
            },
        ],
    },
    {
        title: 'PTZ Limits',
        items: [{ name: 'PTZ limits', id: 'PTZ_limits' }],
    },
    {
        title: 'Initial position',
        items: [
            { name: 'Initial position', id: 'initialPosition' },
            {
                name: 'Scheduled tasks',
                id: 'scheduledTasks',
            },
        ],
    },
    {
        title: 'Auto tracking',
        items: [
            { name: 'Auto basic settings', id: 'autoTracking_BasicSettings' },
            { name: 'Auto schedule settings', id: 'autoTracking_ScheduleSettings' },
        ],
    },
    {
        title: 'Video',
        items: [
            { name: 'Primary stream', id: 'primaryStream' },
            { name: 'Secondary stream', id: 'secondaryStream' },
            { name: 'Tertiary stream', id: 'tertiaryStream' },
        ],
    },
    {
        title: 'Image general',
        items: [
            { name: 'Image adjustment', id: 'imageAdjustment' },
            {
                name: 'Day/Night switch',
                id: 'day_nightSwitch',
            },
            { name: 'Day/Night Parameters', id: 'day_nightParameters' },
            { name: 'Exposure', id: 'exposure' },
            { name: 'Backlight', id: 'backlight' },
            { name: 'White balance', id: 'whiteBalance' },
            { name: 'Image enhancement', id: 'imageEnhancement' },
            { name: 'Display', id: 'display' },
        ],
    },
    {
        title: 'Storage',
        items: [
            {
                name: 'Storage management',
                id: 'storageManagement',
            },
            {
                name: 'Record settings',
                id: 'recordSettings',
            },
            {
                name: 'Snapshot settings',
                id: 'snapshotSettings',
            },
        ],
    },
    {
        title: 'Motion detection',
        items: [
            { name: 'Motion basic settings', id: 'motionDetection_BasicSettings' },
            { name: 'Motion schedule settings', id: 'motionDetection_ScheduleSettings' },
        ],
    },
    {
        title: 'Date & Time',
        items: [
            { name: 'Set the system time', id: 'setTheSystemTime' },
            { name: 'Current system time', id: 'currentSystemTime' },
        ],
    },
];

const menuItemsMileSightCameraSP111: MenuItems[] = [
    // {
    //     title: 'Basic information',
    //     items: [
    //         {
    //             name: 'Basic',
    //             id: 'basic',
    //         },
    //         { name: 'SP111 OSD', id: 'SP111_OSD' },
    //         {
    //             name: 'Others',
    //             id: 'others',
    //         },
    //     ],
    // },
    {
        title: 'SP111 Limits',
        items: [{ name: 'SP111 limits', id: 'SP111_limits' }],
    },
    {
        title: 'Initial position',
        items: [
            { name: 'Initial position', id: 'initialPosition' },
            {
                name: 'Scheduled tasks',
                id: 'scheduledTasks',
            },
        ],
    },
    {
        title: 'Auto tracking',
        items: [
            { name: 'Auto basic settings', id: 'autoTracking_BasicSettings' },
            { name: 'Auto schedule settings', id: 'autoTracking_ScheduleSettings' },
        ],
    },
    {
        title: 'Video',
        items: [
            { name: 'Primary stream', id: 'primaryStream' },
            { name: 'Secondary stream', id: 'secondaryStream' },
            // { name: 'Tertiary stream', id: 'tertiaryStream' },
        ],
    },
    {
        title: 'Image general',
        items: [
            { name: 'Image adjustment', id: 'imageAdjustment' },
            {
                name: 'Day/Night switch',
                id: 'day_nightSwitch',
            },
            { name: 'Day/Night Parameters', id: 'day_nightParameters' },
            { name: 'Exposure', id: 'exposure' },
            { name: 'Backlight', id: 'backlight' },
            { name: 'White balance', id: 'whiteBalance' },
            { name: 'Image enhancement', id: 'imageEnhancement' },
            { name: 'Display', id: 'display' },
        ],
    },
    {
        title: 'Storage',
        items: [
            {
                name: 'Storage management',
                id: 'storageManagement',
            },
            {
                name: 'Record settings',
                id: 'recordSettings',
            },
            {
                name: 'Snapshot settings',
                id: 'snapshotSettings',
            },
        ],
    },
    {
        title: 'Motion detection',
        items: [
            { name: 'Motion basic settings', id: 'motionDetection_BasicSettings' },
            { name: 'Motion schedule settings', id: 'motionDetection_ScheduleSettings' },
        ],
    },
    {
        title: 'Date & Time',
        items: [
            { name: 'Set the system time', id: 'setTheSystemTime' },
            { name: 'Current system time', id: 'currentSystemTime' },
        ],
    },
];

const menuItemsMileSightCameraPMC8266: MenuItems[] = [
    {
        title: 'Parking',
        items: [
            { name: 'Parking management', id: 'parking_management' },
            { name: 'Detection settings', id: 'detection_settings' },
            { name: 'Report settings', id: 'report_settings' },
            { name: 'Alarm settings', id: 'alarm_settings' },
        ],
    },
    {
        title: 'Storage',
        items: [
            {
                name: 'Storage management',
                id: 'storage_management',
            },
            {
                name: 'Record settings',
                id: 'record_settings',
            },
            {
                name: 'Snapshot settings',
                id: 'snapshot_settings',
            },
        ],
    },
    {
        title: 'Motion detection',
        items: [
            { name: 'Motion basic settings', id: 'motionDetection_basicSettings' },
            { name: 'Motion schedule settings', id: 'motionDetection_scheduleSettings' },
        ],
    },
    {
        title: 'Date & Time',
        items: [
            { name: 'Set the system time', id: 'set_the_systemTime' },
            { name: 'Current system time', id: 'current_systemTime' },
        ],
    },
];

export default function TabDetailedInformationOfMilesight() {
    const { loading, camera, filteredCamera, setCamera } = useCameraDetailContext();
    const { isMediumMD, isMobileSM } = useMedia();
    const { solar4gCamera } = useMileSightSolar();
    const queryClient = useQueryClient();
    const { id } = useParams<{ id: string }>();
    const backdrop = useBackdrop();

    const { setIsOpenCollapse: setIsOpenCollapsePTZ, isOpenCollapse: isOpenCollapsePTZ } = useMileSightPTZ({
        filteredCamera,
        cameraPTZ: camera?.PTZCameraId!,
    });

    const { setIsOpenCollapse: setIsOpenCollapseSP111, isOpenCollapse: isOpenCollapseSP111 } = useMileSightSP111({
        filteredCamera,
        cameraSP111: camera?.SP111CameraId!,
    });

    const checkUploadSettingHighlight = (): boolean => {
        if (!solar4gCamera?.Solar4gCaptureInfo) return false;

        const captureInfo = solar4gCamera.Solar4gCaptureInfo;
        const MQTTConfigExpect = MQTTConfigExpectValue(solar4gCamera.Mac);

        return (
            captureInfo?.SnapshotTopic !== MQTTConfigExpect?.SnapshotTopic ||
            captureInfo?.StatusTopic !== MQTTConfigExpect.StatusTopic ||
            captureInfo?.AlarmTopic !== MQTTConfigExpect?.AlarmTopic ||
            captureInfo?.ConfigTopic !== MQTTConfigExpect?.ConfigRequestTopic ||
            captureInfo?.ConfigRespTopic !== MQTTConfigExpect?.ConfigResponseTopic
        );
    };

    const hasUploadSettingHighlight = checkUploadSettingHighlight();

    const menuItemsMileSightCameraSolarWithHighlight: MenuItems[] = menuItemsMileSightCameraSolar.map((menu) => {
        if (menu.title === 'Capture management') {
            return {
                ...menu,
                items: menu.items.map((item) => {
                    if (item.id === 'upload-setting') {
                        return {
                            ...item,
                            highlight: hasUploadSettingHighlight,
                        };
                    }
                    return item;
                }),
            };
        }
        return menu;
    });

    const { setIsOpenCollapse: setIsOpenCollapsePMC8266, isOpenCollapse: isOpenCollapsePMC8266 } = useMileSightPMC8266({
        filteredCamera,
        cameraPMC8266: camera?.PMC8266CameraId!,
    });

    const handleToggleCollapse = (section: SectionsCollapsePTZ) => {
        setIsOpenCollapsePTZ((prev) => ({ ...prev, [section]: !prev[section] }));
    };

    const handleToggleCollapseSP111 = (section: SectionsCollapseSP111) => {
        setIsOpenCollapseSP111((prev) => ({ ...prev, [section]: !prev[section] }));
    };

    const { mutate: syncPTZCameraConfigs } = useMutation({
        mutationFn: async () => {
            backdrop.setTrue();
            try {
                return await ptzCameraHttpController.refreshLatestPTZCameraConfig(
                    camera.Id!
                );
            } finally {
                backdrop.setFalse();
            }
        },
        onSuccess: async (res) => {
            setCamera((prev) => {
                if (!prev.PTZCamera) {
                    return prev;
                }

                return {
                    ...prev,
                    PTZCamera: {
                        ...prev.PTZCamera,
                        PTZCameraConfig: res,
                    },
                };
            });
            pushSuccess('Sync successfully');
        },
        onError: () => {
            pushError('Sync failed');
        },
    });

    const handleSyncManualConfigs = () => {
        const devMac = solar4gCamera?.Mac;
        if (!devMac) {
            pushError('Device MAC address not found');
            return;
        }

        backdrop.setTrue();
        solar4gCameraHttpController
            .refreshLatestInfo(devMac)
            .then(() => {
                queryClient.invalidateQueries(['solar4gCamera', id]);
                pushSuccess('Sync the latest configs successfully');
            })
            .catch((error: any) => {})
            .finally(() => {
                backdrop.setFalse();
            });
    };

    const handleToggleCollapsePMC8266 = (section: SectionsCollapsePMC8266) => {
        setIsOpenCollapsePMC8266((prev) => ({ ...prev, [section]: !prev[section] }));
    };

    const { mutate: syncConfig } = useMutation({
        mutationFn: async () => {
            backdrop.setTrue();
            try {
                return await pmc8266CameraHttpController.refreshLatestPMC8266CameraConfig(
                    camera.Id!
                );
            } finally {
                backdrop.setFalse();
            }
        },
        onSuccess: async (res) => {
            queryClient.invalidateQueries(['trailAuditEvent']);
            setCamera((prev) => {
                if (!prev.PMC8266Camera) {
                    return prev;
                }

                return {
                    ...prev,
                    PMC8266Camera: {
                        ...prev.PMC8266Camera,
                        PMC8266CameraConfigs: res,
                    },
                };
            });
            pushSuccess('Sync configurations successfully');
        },
        onError: () => {
            pushError('Sync failed');
        },
    });

    return (
        <Stack padding={2} borderRadius={'0px 16px 16px 16px'} border={`1px solid #E4E7EC`}>
            {loading ? (
                <Stack justifyContent="center" alignItems="center" minHeight={600}>
                    <CircularProgress />
                </Stack>
            ) : (
                <>
                    {!camera.Deleted &&
                        (camera.PTZCameraId !== null ||
                            camera.VendorCameraId !== null ||
                            camera.PMC8266CameraId !== null) && (
                        <Stack mb={2} gap={1}>
                            <Stack direction="row" justifyContent={'space-between'}>
                                <Typography fontSize={16} variant="h5">
                                    More information
                                </Typography>
                                <Tooltip title="Sync configurations" placement="top" arrow>
                                    <IconButton
                                        sx={{
                                            borderRadius: '3px',
                                            width: '24px',
                                            height: '24px',
                                            color: color.grey600,
                                            border: `1px solid ${color.grey300}`,
                                            '&:hover .rotate-icon': {
                                                transform: 'rotate(90deg) scale(1.1)',
                                            },
                                        }}
                                        onClick={() => {
                                            if (camera.PTZCameraId) syncPTZCameraConfigs();
                                            if (camera.VendorCameraId) handleSyncManualConfigs();
                                            if (camera.PMC8266CameraId) syncConfig();
                                        }}
                                    >
                                        <Autorenew
                                            sx={{
                                                fontSize: 17,
                                                transition: 'transform 0.3s ease-in-out',
                                                color: 'black',
                                            }}
                                            className="rotate-icon"
                                        />
                                    </IconButton>
                                </Tooltip>
                            </Stack>

                            <Divider
                                orientation="vertical"
                                sx={{
                                    borderBottomWidth: 1,
                                    borderColor: color.grey300,
                                }}
                            />
                        </Stack>
                    )}

                    <Grid container justifyContent={'space-between'} gap={2} wrap="nowrap">
                        {isMediumMD ? null : (
                            <Grid
                                item
                                width={'25%'}
                                borderRadius={2}
                                border={`1px solid ${color.grey300}`}
                                sx={{ ...commonScrollbarStyles }}
                            >
                                <BaseMenuListCollapse
                                    menuItems={
                                        camera.PTZCameraId !== null
                                            ? menuItemsMileSightCameraPTZ
                                            : camera.VendorCameraId !== null
                                            ? menuItemsMileSightCameraSolar
                                            : camera.PMC8266CameraId !== null
                                            ? menuItemsMileSightCameraPMC8266
                                            : menuItemsMileSightCameraSP111
                                    }
                                    onSetIsOpenCollapse={
                                        camera.PTZCameraId !== null
                                            ? (fn) =>
                                                setIsOpenCollapsePTZ((prev) => ({
                                                    ...prev,
                                                    ...fn(prev),
                                                }))
                                            : camera.PMC8266CameraId !== null
                                            ? (fn) =>
                                                setIsOpenCollapsePMC8266((prev) => ({
                                                    ...prev,
                                                    ...fn(prev),
                                                }))
                                            : (fn) =>
                                                setIsOpenCollapseSP111((prev) => ({
                                                    ...prev,
                                                    ...fn(prev),
                                                }))
                                    }
                                />
                            </Grid>
                        )}
                        <Grid
                            item
                            width={isMediumMD ? '100%' : '75%'}
                            sx={{ ...commonScrollbarStyles, borderRadius: '8px 0px 0px 8px' }}
                        >
                            {camera.PTZCameraId !== null ? (
                            <MileSightPtzPage
                                isOpenCollapse={isOpenCollapsePTZ}
                                handleToggleCollapse={handleToggleCollapse}
                                onSyncPTZConfigs={syncPTZCameraConfigs}
                            />
                        ) : camera.SP111CameraId !== null ? (
                            <MileSightSP111Page
                                isOpenCollapse={isOpenCollapseSP111}
                                handleToggleCollapse={handleToggleCollapseSP111}
                            />
                        ) : camera.PMC8266CameraId !== null ? (
                            <MileSightPMC8266Page
                                isOpenCollapse={isOpenCollapsePMC8266}
                                handleToggleCollapse={handleToggleCollapsePMC8266}
                            />
                        ) : (
                            <Box>
                                <CellularMilesightCameraSolar />
                                <StatusManagementMilesightCameraSolar />
                                <CaptureManagementMilesightCameraSolar camera={camera} />
                                <DateAndTimeMilesightCameraSolar />
                            </Box>
                        )}
                        </Grid>
                    </Grid>
                </>
            )}
        </Stack>
    );
}

const commonScrollbarStyles: SxProps = {
    maxHeight: 560,
    overflowY: 'auto',
    overflowX: 'auto',
    '::-webkit-scrollbar': {
        width: '2px',
    },
    '::-webkit-scrollbar-track': {
        background: 'none',
    },
    '::-webkit-scrollbar-thumb': {
        background: 'none',
    },
};
